import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, CreditCard, Banknote } from 'lucide-react';
import { toast } from 'sonner';

export default function FnBPaymentDialog({ order, open, onClose, onSuccess }) {
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountReceived, setAmountReceived] = useState('');
  const [change, setChange] = useState(0);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (open && order) {
      setPaymentMethod('cash');
      setAmountReceived('');
      setChange(0);
    }
  }, [open, order]);

  useEffect(() => {
    if (paymentMethod === 'cash' && amountReceived && order) {
      const received = parseFloat(amountReceived) || 0;
      const total = order.total || 0;
      setChange(Math.max(0, received - total));
    } else {
      setChange(0);
    }
  }, [amountReceived, paymentMethod, order]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!order) return;

    if (paymentMethod === 'cash') {
      const received = parseFloat(amountReceived) || 0;
      if (received < order.total) {
        toast.error('Uang yang diterima kurang!');
        return;
      }
    }

    setProcessing(true);
    try {
      await onSuccess(
        order.id,
        paymentMethod,
        paymentMethod === 'cash' ? parseFloat(amountReceived) : order.total
      );
      onClose();
    } catch (error) {
      console.error('Payment failed:', error);
    } finally {
      setProcessing(false);
    }
  };

  if (!open || !order) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto p-4"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-6 flex items-center justify-between rounded-t-2xl">
          <div>
            <h3 className="text-xl font-bold text-white">Pembayaran F&B</h3>
            <p className="text-blue-100 text-sm mt-1">{order.customer_name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Order Summary */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
            <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-1 h-5 bg-blue-500 rounded-full"></span>
              Detail Pesanan
            </h4>
            <div className="space-y-2">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center text-sm bg-white px-3 py-2 rounded-lg">
                  <span className="text-gray-700 font-medium">
                    <span className="inline-block w-8 text-center bg-blue-100 text-blue-700 rounded px-1 text-xs font-bold mr-2">
                      {item.quantity}x
                    </span>
                    {item.name}
                  </span>
                  <span className="text-gray-900 font-semibold">Rp {(item.price * item.quantity).toLocaleString('id-ID')}</span>
                </div>
              ))}
            </div>
            <div className="border-t-2 border-gray-300 mt-4 pt-3 flex justify-between items-center">
              <span className="font-bold text-gray-800 text-lg">Total Tagihan:</span>
              <span className="text-3xl font-black text-blue-600">
                Rp {order.total.toLocaleString('id-ID')}
              </span>
            </div>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-bold text-gray-800 mb-3">
               Metode Pembayaran
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setPaymentMethod('cash')}
                className={`p-4 rounded-xl border-2 transition-all transform hover:scale-105 ${
                  paymentMethod === 'cash'
                    ? 'border-green-500 bg-gradient-to-br from-green-50 to-green-100 shadow-lg shadow-green-200'
                    : 'border-gray-300 bg-white hover:border-gray-400 hover:shadow-md'
                }`}
              >
                <Banknote className={`h-6 w-6 mx-auto mb-2 ${paymentMethod === 'cash' ? 'text-green-600' : 'text-gray-400'}`} />
                <span className={`text-sm font-bold ${paymentMethod === 'cash' ? 'text-green-700' : 'text-gray-600'}`}>Cash</span>
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod('qris')}
                className={`p-4 rounded-xl border-2 transition-all transform hover:scale-105 ${
                  paymentMethod === 'qris'
                    ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-purple-100 shadow-lg shadow-purple-200'
                    : 'border-gray-300 bg-white hover:border-gray-400 hover:shadow-md'
                }`}
              >
                <CreditCard className={`h-6 w-6 mx-auto mb-2 ${paymentMethod === 'qris' ? 'text-purple-600' : 'text-gray-400'}`} />
                <span className={`text-sm font-bold ${paymentMethod === 'qris' ? 'text-purple-700' : 'text-gray-600'}`}>QRIS</span>
              </button>
            </div>
          </div>

          {/* Amount Received (only for cash) */}
          {paymentMethod === 'cash' && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-200">
              <label className="block text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <span className="text-xl"></span>
                UANG DITERIMA
              </label>
              
              {/* Quick Cash Buttons */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setAmountReceived(order.total.toString())}
                  className="bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-6 px-4 rounded-xl shadow-lg transition-all transform hover:scale-105 active:scale-95 flex flex-col items-center justify-center"
                >
                  <Banknote className="h-5 w-5 mb-1" />
                  <span className="text-sm">Pas</span>
                </button>
                <button
                  type="button"
                  onClick={() => setAmountReceived('10000')}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-6 px-4 rounded-xl shadow transition-all transform hover:scale-105 active:scale-95 border-2 border-gray-300"
                >
                  <div className="text-center">
                    <div className="text-xs text-gray-600">Rp</div>
                    <div className="text-lg font-black">10K</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setAmountReceived('20000')}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-6 px-4 rounded-xl shadow transition-all transform hover:scale-105 active:scale-95 border-2 border-gray-300"
                >
                  <div className="text-center">
                    <div className="text-xs text-gray-600">Rp</div>
                    <div className="text-lg font-black">20K</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setAmountReceived('50000')}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-6 px-4 rounded-xl shadow transition-all transform hover:scale-105 active:scale-95 border-2 border-gray-300"
                >
                  <div className="text-center">
                    <div className="text-xs text-gray-600">Rp</div>
                    <div className="text-lg font-black">50K</div>
                  </div>
                </button>
              </div>

              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg">Rp</span>
                <input
                  type="number"
                  value={amountReceived}
                  onChange={(e) => setAmountReceived(e.target.value)}
                  placeholder="0"
                  className="w-full pl-12 pr-4 py-4 border-2 border-green-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-2xl font-bold text-gray-800 bg-white"
                  required
                  min={order.total}
                  step="1000"
                />
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Minimal: Rp {order.total.toLocaleString('id-ID')}
              </p>
            </div>
          )}

          {/* Change */}
          {paymentMethod === 'cash' && change > 0 && (
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-xl p-5 animate-pulse">
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-yellow-800 font-bold text-lg flex items-center gap-2">
                    <span className="text-2xl"></span>
                    Kembalian
                  </span>
                  <p className="text-xs text-yellow-700 mt-1">Berikan kepada customer</p>
                </div>
                <span className="text-4xl font-black text-yellow-600">
                  Rp {change.toLocaleString('id-ID')}
                </span>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={processing || (paymentMethod === 'cash' && (!amountReceived || parseFloat(amountReceived) < order.total))}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-lg transition-all shadow-lg"
          >
            {processing ? 'Memproses...' : 'Konfirmasi Pembayaran'}
          </button>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
